// features/procurement/VendorDetailPage.tsx
/**
 * Vendor / Supplier Detail — Redesigned Executive Profile
 * Features:
 * - Commercial scorecard: Total Purchase Volume, Orders Count, Lead Time, Rating
 * - 1-Click "Create Purchase Order" shortcut for instant supplier ordering
 * - 3-Tab structured Edit Vendor modal (General, Contact & Location, Commercial & Banking)
 * - Purchase Order History table with direct status pills and navigation
 * - Spring-animated Deactivate / Delete confirmation modals
 * - Zero silent failures with explicit notifications
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Pencil, Trash2, Star, Phone, Mail, MapPin,
    Building2, CreditCard, Clock, Truck, FileText, Package,
    DollarSign, Ban, CheckCircle2, Globe, Hash, User,
    Store, Plus, AlertTriangle, X, ShieldCheck, ExternalLink,
    Sparkles, Copy, Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { getProcurementErrorMessage } from './services/procurementApi';

interface Vendor {
    id: string;
    name: string;
    contactPerson: string | null;
    email: string | null;
    phone: string | null;
    alternatePhone: string | null;
    website: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    country: string;
    taxId: string | null;
    registrationNumber: string | null;
    paymentTerms: string | null;
    creditLimit: string | null;
    currency: string;
    bankName: string | null;
    bankAccountNumber: string | null;
    bankAccountName: string | null;
    bankSortCode: string | null;
    category: string | null;
    leadTimeDays: number;
    minimumOrderAmount: string | null;
    accountNumber: string | null;
    rating: number;
    isPreferred: boolean;
    isActive: boolean;
    notes: string | null;
    tags: string | null;
    createdAt: string;
    _count?: { purchaseOrders: number };
    totalPurchases?: string;
    lastOrderDate?: string;
}

const StarRating = ({ rating, size = 16 }: { rating: number; size?: number }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
            <Star
                key={star}
                size={size}
                className={clsx(star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-700')}
            />
        ))}
    </div>
);

const PO_STATUS_STYLES: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-teal-50 text-teal-700 border-teal-200',
    sent: 'bg-purple-50 text-purple-700 border-purple-200',
    partially_received: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    fully_received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function VendorDetailPage() {
    const { vendorId } = useParams<{ vendorId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editTab, setEditTab] = useState<'general' | 'contact' | 'commercial'>('general');
    const [editForm, setEditForm] = useState<any>({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: vendor, isLoading, isError, refetch } = useQuery<Vendor>({
        queryKey: ['vendor-detail', vendorId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/vendors/${vendorId}`);
            return data.data;
        },
        enabled: !!user?.tenantId && !!vendorId,
    });

    const { data: purchaseOrders } = useQuery<any[]>({
        queryKey: ['vendor-pos', vendorId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/purchase-orders`, {
                params: { vendorId, limit: 20, sortBy: 'createdAt', order: 'desc' },
            });
            return data.data || [];
        },
        enabled: !!user?.tenantId && !!vendorId,
    });

    // ============================================================
    // MUTATIONS
    // ============================================================

    const updateMutation = useMutation({
        mutationFn: (payload: any) =>
            apiClient.patch(`/tenants/${user!.tenantId}/vendors/${vendorId}`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendor-detail', vendorId] });
            queryClient.invalidateQueries({ queryKey: ['vendors-po'] });
            queryClient.invalidateQueries({ queryKey: ['vendors-grn'] });
            setShowEditModal(false);
            addToast({
                type: 'success',
                title: 'Vendor Updated',
                message: `${editForm.name || 'Vendor profile'} has been refreshed.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Update Failed',
                message: getProcurementErrorMessage(err, 'Failed to update vendor information.'),
            });
        },
    });

    const toggleStatusMutation = useMutation({
        mutationFn: (isActive: boolean) =>
            apiClient.patch(`/tenants/${user!.tenantId}/vendors/${vendorId}`, { isActive }),
        onSuccess: (_, isActive) => {
            queryClient.invalidateQueries({ queryKey: ['vendor-detail', vendorId] });
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-vendors'] });
            addToast({
                type: 'success',
                title: 'Status Updated',
                message: `Vendor is now ${isActive ? 'Active' : 'Deactivated'}.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Status Change Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => apiClient.delete(`/tenants/${user!.tenantId}/vendors/${vendorId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-vendors'] });
            addToast({
                type: 'success',
                title: 'Vendor Removed',
                message: 'Vendor has been archived from supplier directory.',
            });
            navigate('/vendors');
        },
        onError: (err: any) => {
            setShowDeleteModal(false);
            addToast({
                type: 'error',
                title: 'Delete Failed',
                message: getProcurementErrorMessage(err, 'Cannot remove vendor with active purchase orders.'),
            });
        },
    });

    // ============================================================
    // HELPERS
    // ============================================================

    const copyToClipboard = (text: string, fieldName: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
        addToast({
            type: 'info',
            title: 'Copied to Clipboard',
            message: `${fieldName}: ${text}`,
            duration: 2000,
        });
    };

    const formatNaira = (val: string | number | null | undefined): string => {
        if (!val) return '₦0.00';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr: string): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const handleOpenEdit = () => {
        if (!vendor) return;
        setEditForm({
            name: vendor.name,
            contactPerson: vendor.contactPerson || '',
            email: vendor.email || '',
            phone: vendor.phone || '',
            alternatePhone: vendor.alternatePhone || '',
            website: vendor.website || '',
            address: vendor.address || '',
            city: vendor.city || '',
            state: vendor.state || '',
            country: vendor.country || 'Nigeria',
            taxId: vendor.taxId || '',
            registrationNumber: vendor.registrationNumber || '',
            paymentTerms: vendor.paymentTerms || '',
            creditLimit: vendor.creditLimit || '',
            bankName: vendor.bankName || '',
            bankAccountNumber: vendor.bankAccountNumber || '',
            bankAccountName: vendor.bankAccountName || '',
            bankSortCode: vendor.bankSortCode || '',
            category: vendor.category || '',
            leadTimeDays: vendor.leadTimeDays || 7,
            minimumOrderAmount: vendor.minimumOrderAmount || '',
            rating: vendor.rating || 5,
            notes: vendor.notes || '',
        });
        setEditTab('general');
        setShowEditModal(true);
    };

    const handleSaveEdit = () => {
        if (!editForm.name?.trim()) return;
        const payload: any = {
            name: editForm.name.trim(),
            contactPerson: editForm.contactPerson?.trim() || null,
            email: editForm.email?.trim() || null,
            phone: editForm.phone?.trim() || null,
            alternatePhone: editForm.alternatePhone?.trim() || null,
            website: editForm.website?.trim() || null,
            address: editForm.address?.trim() || null,
            city: editForm.city?.trim() || null,
            state: editForm.state?.trim() || null,
            country: editForm.country?.trim() || 'Nigeria',
            taxId: editForm.taxId?.trim() || null,
            registrationNumber: editForm.registrationNumber?.trim() || null,
            paymentTerms: editForm.paymentTerms?.trim() || null,
            creditLimit: editForm.creditLimit ? String(editForm.creditLimit) : null,
            bankName: editForm.bankName?.trim() || null,
            bankAccountNumber: editForm.bankAccountNumber?.trim() || null,
            bankAccountName: editForm.bankAccountName?.trim() || null,
            bankSortCode: editForm.bankSortCode?.trim() || null,
            category: editForm.category?.trim() || null,
            leadTimeDays: parseInt(String(editForm.leadTimeDays)) || 7,
            minimumOrderAmount: editForm.minimumOrderAmount ? String(editForm.minimumOrderAmount) : null,
            rating: parseInt(String(editForm.rating)) || 5,
            notes: editForm.notes?.trim() || null,
        };
        updateMutation.mutate(payload);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50/60 text-slate-600 gap-4">
                <Spinner size="lg" variant="teal" />
                <p className="text-sm font-semibold tracking-wide text-slate-700">Loading vendor profile...</p>
            </div>
        );
    }

    if (isError || !vendor) {
        return (
            <div className="min-h-screen bg-slate-50/60 flex items-center justify-center p-6">
                <ErrorState
                    title="Supplier Profile Not Found"
                    message="The requested vendor record could not be loaded or has been deleted."
                    onBack={() => navigate('/vendors')}
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    const posList = purchaseOrders || [];
    const totalPosValue = posList.reduce((sum, po) => sum + parseFloat(po.totalAmount || '0'), 0);

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-800 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/vendors')}
                                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to Vendors Directory"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/20 text-white">
                                <Store className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                        {vendor.name}
                                    </h1>
                                    {vendor.isPreferred && (
                                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider">
                                            ★ Preferred Supplier
                                        </span>
                                    )}
                                    <span className={clsx(
                                        'px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider',
                                        vendor.isActive
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            : 'bg-rose-50 text-rose-700 border-rose-200'
                                    )}>
                                        {vendor.isActive ? 'Active' : 'Deactivated'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                    <StarRating rating={vendor.rating} size={13} />
                                    <span>•</span>
                                    <span className="text-slate-700 font-medium">{vendor.category || 'General Supplies'}</span>
                                    {vendor.country && (
                                        <>
                                            <span>•</span>
                                            <span>{vendor.city ? `${vendor.city}, ` : ''}{vendor.country}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Top Action CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
                            <button
                                onClick={() => navigate(`/procurement/orders/new`)}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-teal-500/20 transition-all cursor-pointer"
                                type="button"
                            >
                                <Plus className="w-4 h-4" />
                                <span>Create Purchase Order</span>
                            </button>

                            <button
                                onClick={handleOpenEdit}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-xl font-bold text-xs transition-all shadow-sm cursor-pointer"
                                type="button"
                            >
                                <Pencil className="w-4 h-4 text-slate-500" />
                                <span>Edit Profile</span>
                            </button>

                            <button
                                onClick={() => toggleStatusMutation.mutate(!vendor.isActive)}
                                disabled={toggleStatusMutation.isPending}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-xl font-bold text-xs transition-all cursor-pointer disabled:opacity-50"
                                type="button"
                            >
                                {vendor.isActive ? (
                                    <>
                                        <Ban className="w-4 h-4 text-amber-600" />
                                        <span>Deactivate</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                        <span>Activate</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="p-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-600 rounded-xl transition-all cursor-pointer"
                                type="button"
                                title="Delete Vendor"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Scorecards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                            Total Orders Issued
                        </span>
                        <p className="text-2xl font-black text-slate-900">{posList.length}</p>
                        <p className="text-xs text-slate-500 mt-1">Direct PO fulfillment contracts</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-teal-600 uppercase tracking-wider block mb-1">
                            Total Purchase Volume
                        </span>
                        <p className="text-2xl font-black text-teal-600">{formatNaira(totalPosValue)}</p>
                        <p className="text-xs text-slate-500 mt-1">Cumulative spend across branches</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-purple-600 uppercase tracking-wider block mb-1">
                            Contract Lead Time
                        </span>
                        <p className="text-2xl font-black text-purple-600">{vendor.leadTimeDays || 7} Days</p>
                        <p className="text-xs text-slate-500 mt-1">Average delivery SLA window</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider block mb-1">
                            Vendor Quality Rating
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-2xl font-black text-amber-600">{vendor.rating || 5}.0</span>
                            <StarRating rating={vendor.rating} size={15} />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Evaluated on delivery timeliness & quality</p>
                    </div>
                </div>

                {/* Profile Grid: Contact, Commercial & Banking */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Contact Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 text-teal-700 font-bold text-sm">
                            <Phone className="w-4 h-4" />
                            <span>Contact & Location</span>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Contact Person</span>
                                <p className="font-semibold text-slate-900 mt-0.5">{vendor.contactPerson || 'Not provided'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Email Address</span>
                                {vendor.email ? (
                                    <div className="flex items-center justify-between mt-0.5">
                                        <a href={`mailto:${vendor.email}`} className="text-teal-600 hover:underline truncate">
                                            {vendor.email}
                                        </a>
                                        <button
                                            onClick={() => copyToClipboard(vendor.email!, 'Email')}
                                            className="text-slate-400 hover:text-slate-600 p-1"
                                            type="button"
                                        >
                                            {copiedField === 'Email' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-slate-400">—</p>
                                )}
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Telephone</span>
                                {vendor.phone ? (
                                    <div className="flex items-center justify-between mt-0.5">
                                        <a href={`tel:${vendor.phone}`} className="text-slate-900 hover:text-teal-600 font-mono">
                                            {vendor.phone}
                                        </a>
                                        <button
                                            onClick={() => copyToClipboard(vendor.phone!, 'Phone')}
                                            className="text-slate-400 hover:text-slate-600 p-1"
                                            type="button"
                                        >
                                            {copiedField === 'Phone' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-slate-400">—</p>
                                )}
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Physical Address</span>
                                <p className="text-slate-700 mt-0.5 leading-relaxed">{vendor.address || '—'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Commercial Terms Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 text-purple-700 font-bold text-sm">
                            <Building2 className="w-4 h-4" />
                            <span>Commercial & Tax ID</span>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Payment Terms</span>
                                <p className="font-semibold text-slate-900 mt-0.5">{vendor.paymentTerms || 'Net 30 days'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Tax ID / TIN</span>
                                <p className="font-mono text-slate-700 mt-0.5">{vendor.taxId || 'Not recorded'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">CAC / Registration #</span>
                                <p className="font-mono text-slate-700 mt-0.5">{vendor.registrationNumber || '—'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Credit Limit</span>
                                <p className="font-semibold text-slate-900 mt-0.5">
                                    {vendor.creditLimit ? formatNaira(vendor.creditLimit) : 'Unlimited / Standard'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Banking Details Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 text-cyan-700 font-bold text-sm">
                            <CreditCard className="w-4 h-4" />
                            <span>Banking & Settlement</span>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Bank Name</span>
                                <p className="font-semibold text-slate-900 mt-0.5">{vendor.bankName || 'Not recorded'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Account Number</span>
                                {vendor.bankAccountNumber ? (
                                    <div className="flex items-center justify-between mt-0.5">
                                        <span className="font-mono text-slate-900 font-bold text-sm tracking-wider">{vendor.bankAccountNumber}</span>
                                        <button
                                            onClick={() => copyToClipboard(vendor.bankAccountNumber!, 'Account Number')}
                                            className="text-slate-400 hover:text-slate-600 p-1"
                                            type="button"
                                        >
                                            {copiedField === 'Account Number' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-slate-400">—</p>
                                )}
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Account Name</span>
                                <p className="text-slate-700 mt-0.5">{vendor.bankAccountName || '—'}</p>
                            </div>

                            <div>
                                <span className="text-slate-500 uppercase tracking-wider font-bold block text-[10px]">Sort Code / Swift</span>
                                <p className="font-mono text-slate-600 mt-0.5">{vendor.bankSortCode || '—'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchase Order History Table */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Truck className="w-5 h-5 text-teal-600" />
                                <span>Order History ({posList.length})</span>
                            </h3>
                            <p className="text-xs text-slate-500">All purchase order agreements executed with this supplier</p>
                        </div>
                        <button
                            onClick={() => navigate('/procurement/orders/new')}
                            className="px-4 py-2 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                            type="button"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Issue New PO</span>
                        </button>
                    </div>

                    {posList.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 text-xs">
                            No purchase orders issued to this vendor yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/50">
                                        <th className="py-3 px-4">PO Number</th>
                                        <th className="py-3 px-4">Date</th>
                                        <th className="py-3 px-4">Branch</th>
                                        <th className="py-3 px-4 text-center">Status</th>
                                        <th className="py-3 px-4 text-right">Commitment</th>
                                        <th className="py-3 px-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {posList.map((po) => (
                                        <tr
                                            key={po.id}
                                            onClick={() => navigate(`/procurement/orders/${po.id}`)}
                                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                                        >
                                            <td className="py-4 px-4 font-mono font-bold text-teal-600">
                                                #{po.poNumber}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-500">
                                                {formatDate(po.createdAt)}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-700">
                                                {po.branchName || 'Main Facility'}
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <span className={clsx(
                                                    'px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border',
                                                    PO_STATUS_STYLES[po.status] || 'bg-slate-100 text-slate-700 border-slate-200'
                                                )}>
                                                    {po.status?.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono font-bold text-slate-900">
                                                {formatNaira(po.totalAmount)}
                                            </td>
                                            <td className="py-4 px-4 text-right text-xs text-teal-600 font-bold">
                                                View →
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ========== EDIT VENDOR MODAL ========== */}
            <AnimatePresence>
                {showEditModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowEditModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-teal-50 text-teal-600">
                                        <Pencil className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">Edit Vendor Profile</h2>
                                        <p className="text-xs text-slate-500">Modify commercial, contact, and banking records</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                    type="button"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="flex items-center border-b border-slate-200 bg-slate-50/80 px-6">
                                {[
                                    { id: 'general', label: 'Company Info' },
                                    { id: 'contact', label: 'Contact & Location' },
                                    { id: 'commercial', label: 'Commercial & Bank' },
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setEditTab(tab.id as any)}
                                        className={clsx(
                                            'py-3 px-4 text-xs font-bold border-b-2 transition-all',
                                            editTab === tab.id
                                                ? 'border-teal-600 text-teal-700'
                                                : 'border-transparent text-slate-500 hover:text-slate-800'
                                        )}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="p-6 overflow-y-auto space-y-4 flex-1">
                                {editTab === 'general' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Company / Vendor Name *</label>
                                            <input
                                                type="text"
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                required
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Industry Category</label>
                                                <input
                                                    type="text"
                                                    value={editForm.category}
                                                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                                    placeholder="e.g. Raw Materials, Packaging"
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Lead Time (Days)</label>
                                                <input
                                                    type="number"
                                                    value={editForm.leadTimeDays}
                                                    onChange={(e) => setEditForm({ ...editForm, leadTimeDays: parseInt(e.target.value) || 0 })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Notes</label>
                                            <textarea
                                                value={editForm.notes}
                                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                                rows={2}
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 resize-none placeholder:text-slate-400"
                                            />
                                        </div>
                                    </div>
                                )}

                                {editTab === 'contact' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Contact Person</label>
                                                <input
                                                    type="text"
                                                    value={editForm.contactPerson}
                                                    onChange={(e) => setEditForm({ ...editForm, contactPerson: e.target.value })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Primary Phone</label>
                                                <input
                                                    type="text"
                                                    value={editForm.phone}
                                                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Email Address</label>
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Address</label>
                                            <input
                                                type="text"
                                                value={editForm.address}
                                                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                            />
                                        </div>
                                    </div>
                                )}

                                {editTab === 'commercial' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Payment Terms</label>
                                                <input
                                                    type="text"
                                                    value={editForm.paymentTerms}
                                                    onChange={(e) => setEditForm({ ...editForm, paymentTerms: e.target.value })}
                                                    placeholder="e.g. Net 30, Cash on Delivery"
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Tax Identification (TIN)</label>
                                                <input
                                                    type="text"
                                                    value={editForm.taxId}
                                                    onChange={(e) => setEditForm({ ...editForm, taxId: e.target.value })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 font-mono placeholder:text-slate-400"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Bank Name</label>
                                                <input
                                                    type="text"
                                                    value={editForm.bankName}
                                                    onChange={(e) => setEditForm({ ...editForm, bankName: e.target.value })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Bank Account #</label>
                                                <input
                                                    type="text"
                                                    value={editForm.bankAccountNumber}
                                                    onChange={(e) => setEditForm({ ...editForm, bankAccountNumber: e.target.value })}
                                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 font-mono placeholder:text-slate-400"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1.5">Account Beneficiary Name</label>
                                            <input
                                                type="text"
                                                value={editForm.bankAccountName}
                                                onChange={(e) => setEditForm({ ...editForm, bankAccountName: e.target.value })}
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-teal-600 placeholder:text-slate-400"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50/50">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-xs transition-all"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={updateMutation.isPending || !editForm.name?.trim()}
                                    className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-teal-500/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                    type="button"
                                >
                                    {updateMutation.isPending ? <Spinner size="xs" variant="white" /> : 'Save Changes'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ========== DELETE CONFIRMATION MODAL ========== */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center space-y-5"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                                <AlertTriangle className="w-8 h-8" />
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-slate-900">Delete Supplier Profile?</h2>
                                <p className="text-xs text-slate-500 mt-2">
                                    Are you sure you want to delete <strong>{vendor.name}</strong>? This action cannot be reversed if historical purchase orders exist.
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => deleteMutation.mutate()}
                                    disabled={deleteMutation.isPending}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 font-bold text-sm shadow-xl shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                    type="button"
                                >
                                    {deleteMutation.isPending ? <Spinner size="xs" variant="white" /> : 'Delete Supplier'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}